import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { lastValueFrom, Subscription } from 'rxjs';
import moment from 'moment';

import {
  DynamicListingComponent,
  FilterConfig,
  ColumnConfig,
  ActionConfig,
  ActionEvent,
  FilterEvent
} from '../dynamic-listing/dynamic-listing';
import { SVG_ICONS } from '../utils/svg-icons.utils';
import { ClientTypeaheadUtils } from '../utils/client-typeahead.utils';
import { ClientOrderService } from '../services/client/client-order.service';
import { ClientListService } from '../services/client/client-list.service';
import { SubsidiaryService } from '../services/subsidiary/subsidiary.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { MastersService } from '../services/masters/masters.service';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ExportDialogComponent } from '../export-dialog/export-dialog.component';
import { ExportApiService, CreateExportJobRequest } from '../services/export/export-api.service';
import { openInNewTab } from '../utils/url.utils';
import { ListStateService } from '../services/list-state/list-state.service';

@Component({
  selector: 'lib-client-so-list',
  standalone: true,
  imports: [CommonModule, DynamicListingComponent, DynamicDialogModule],
  providers: [DialogService],
  templateUrl: './client-so-list.html',
  styleUrl: './client-so-list.css',
})
export class ClientSoList implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly listStateService = inject(ListStateService);
  private pageSubscription?: Subscription;

  exportFilename = `client_sales_orders_${new Date().toISOString().slice(0, 10)}`;

  tableData: any[] = [];
  totalCount = 0;
  totalPages = 0;
  isLoading = false;
  isLoadingClients = false;
  pageIndex = 1;
  rowsPerPage = 10;
  columnPreferenceId?: string;

  orderNumber: string = '';
  fromDate: Date | null = null;
  toDate: Date | null = null;
  receiveByDate: Date | null = null;
  selectedClient: string = '';
  priorityLevel: string = '';
  selectedLocation: string = '';

  clientOptions: any[] = [];
  clientSuggestions: any[] = [];
  locationOptions: { value: string; label: string }[] = [];
  isLoadingLocations = false;

  filterConfigs: FilterConfig[] = [];

  statusOptions = [
    { label: 'Open', value: 'open' },
    { label: 'Partially Complete', value: 'partially_complete' },
    { label: 'Partially Billed', value: 'partially_billed' },
    { label: 'Billed', value: 'billed' },
    { label: 'Closed', value: 'closed' },
  ];
  status: string = '';

  columnConfigs: ColumnConfig[] = [
    { key: 'order_no', header: 'SO #' },
    { key: 'order_date', header: 'Order Date' },
    { key: 'client_code', header: 'Client Code' },
    { key: 'client_name', header: 'Client Name' },
    { key: 'project', header: 'Project' },
    { key: 'location', header: 'Location' },
    { key: 'gross_amount', header: 'Gross Amount' },
    { key: 'final_amount', header: 'Total Amount' },
    { key: 'memo', header: 'Memo', ellipsis: true },
    { key: 'status', header: 'Status', type: 'status' },
    { key: 'approval_status', header: 'Approval Status' }
  ];

  actionConfigs: ActionConfig[] = [
    {
      key: 'view',
      label: 'View',
      tooltip: 'View',
      severity: 'secondary',
      svgIcon: SVG_ICONS.VIEW
    },
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT,
      disabled: (row: any) => {
        const approvalStatus = String(row?.approval_status || '').toLowerCase();
        const status = String(row?.status || row?.raw?.status || '').toLowerCase();
        const isCancelled = row?.is_cancelled || row?.raw?.is_cancelled;
        return isCancelled || approvalStatus === 'approved' || status === 'closed' || status === 'billed';
      },
      disabledTooltip: (row: any) => {
        const isCancelled = row?.is_cancelled || row?.raw?.is_cancelled;
        const status = String(row?.status || row?.raw?.status || '').toLowerCase();
        if (isCancelled) return 'Cannot edit cancelled order';
        if (status === 'closed' || status === 'billed') return 'Cannot edit order with closed or billed status';
        return 'Cannot edit order with approved status';
      }
    },
    {
      key: 'createInvoice',
      label: 'Create Invoice',
      tooltip: 'Create Sales Invoice',
      severity: 'secondary',
      icon: 'pi pi-file',
      disabled: (row: any) => {
        const approvalStatus = String(row?.approval_status || '').toLowerCase();
        const status = String(row?.status || row?.raw?.status || '').toLowerCase();
        const isCancelled = row?.is_cancelled || row?.raw?.is_cancelled;
        return isCancelled || !['approved', 'not_required', 'not required'].includes(approvalStatus) || status === 'closed' || status === 'billed';
      },
      disabledTooltip: (row: any) => {
        const status = String(row?.status || row?.raw?.status || '').toLowerCase();
        const isCancelled = row?.is_cancelled || row?.raw?.is_cancelled;
        if (isCancelled) return 'Cannot create invoice for cancelled order';
        if (status === 'closed' || status === 'billed') {
          return 'Cannot create invoice for closed or billed order';
        }
        return 'Order must be approved to create invoice';
      }
    },
    {
      key: 'delete',
      label: 'Delete',
      tooltip: 'Delete',
      severity: 'secondary',
      svgIcon: SVG_ICONS.DELETE,
      disabled: (row: any) => {
        const approvalStatus = String(row?.approval_status || '').toLowerCase();
        const status = String(row?.status || row?.raw?.status || '').toLowerCase();
        const isCancelled = row?.is_cancelled || row?.raw?.is_cancelled;
        return isCancelled || approvalStatus === 'approved' || status === 'closed' || status === 'billed';
      },
      disabledTooltip: (row: any) => {
        const isCancelled = row?.is_cancelled || row?.raw?.is_cancelled;
        const status = String(row?.status || row?.raw?.status || '').toLowerCase();
        if (isCancelled) return 'Cannot delete cancelled order';
        if (status === 'closed' || status === 'billed') return 'Cannot delete order with closed or billed status';
        return 'Cannot delete order with approved status';
      }
    },
    {
      key: 'cancel',
      label: 'Cancel',
      tooltip: 'Cancel',
      severity: 'secondary',
      svgIcon: SVG_ICONS.CROSS_ICON,
      disabled: (row: any) => {
        const approvalStatus = String(row?.approval_status || '').toLowerCase();
        const status = String(row?.status || row?.raw?.status || '').toLowerCase();
        const isCancelled = row?.is_cancelled || row?.raw?.is_cancelled;
        return isCancelled || approvalStatus === 'approved' || status === 'closed' || status === 'billed';
      },
      disabledTooltip: (row: any) => {
        const isCancelled = row?.is_cancelled || row?.raw?.is_cancelled;
        const status = String(row?.status || row?.raw?.status || '').toLowerCase();
        if (isCancelled) return 'Order is already cancelled';
        if (status === 'closed' || status === 'billed') return 'Cannot cancel order with closed or billed status';
        return 'Cannot cancel order with approved status';
      }
    }
  ];

  constructor(
    private router: Router,
    private clientOrderService: ClientOrderService,
    private clientListService: ClientListService,
    private subsidiaryService: SubsidiaryService,
    private toasterService: ToasterService,
    private dialogService: DialogService,
    private mastersService: MastersService,
    private exportApiService: ExportApiService
  ) {}

  ngOnInit() {
    this.initializeFilterConfigs();
    Promise.all([this.loadClients(), this.loadLocations()]).then(() => {
      this.initializeFilterConfigs();
    });
    // Subscribe to URL page changes
    this.pageSubscription = this.listStateService.getPageFromRoute(this.route).subscribe(page => {
      this.loadClientOrders(page);
    });
  }

  ngOnDestroy() {
    this.pageSubscription?.unsubscribe();
  }

  async loadLocations() {
    try {
      this.isLoadingLocations = true;
      const response = await lastValueFrom(this.subsidiaryService.getSubsidiaries());
      const subsidiaries = response?.data?.subsidiaries || response?.data || [];

      // Extract all location addresses from all subsidiaries
      const allLocations: { value: string; label: string }[] = [];
      (Array.isArray(subsidiaries) ? subsidiaries : []).forEach((subsidiary: any) => {
        const addresses = subsidiary?.tax_information || [];
        (Array.isArray(addresses) ? addresses : []).forEach((addr: any) => {
          if (addr?.id && !addr?.is_deleted) {
            allLocations.push({
              value: String(addr.id),
              label: `${subsidiary.name || 'Subsidiary'} - ${addr.address || addr.gst_number || 'Location'}`,
            });
          }
        });
      });

      this.locationOptions = allLocations;
      this.initializeFilterConfigs();
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      this.isLoadingLocations = false;
    }
  }

  initializeFilterConfigs() {
    this.filterConfigs = [
      {
        key: 'fromDate',
        type: 'date',
        placeholder: 'From Date',
        value: this.fromDate,
        showInAccordion: true,
        showInDialog: false
      },
      {
        key: 'toDate',
        type: 'date',
        placeholder: 'To Date',
        value: this.toDate,
        showInAccordion: true,
        showInDialog: false
      },
      {
        key: 'orderNumber',
        type: 'text',
        placeholder: 'SO number',
        value: this.orderNumber,
        showInAccordion: true,
        showInDialog: false
      },
      {
        key: 'selectedClient',
        type: 'autocomplete',
        placeholder: 'Search Client',
        options: this.clientSuggestions,
        optionLabel: 'label',
        optionValue: 'value',
        value: this.getClientFilterValue(),
        dropdown: true,
        forceSelection: true,
        showInAccordion: true,
        showInDialog: false,
        completeMethod: (event) => this.handleClientSearch(event)
      },
      {
        key: 'status',
        type: 'select',
        placeholder: 'Status',
        options: this.statusOptions,
        optionLabel: 'label',
        optionValue: 'value',
        value: this.status,
        showInAccordion: true,
        showInDialog: false
      },
      {
        key: 'selectedLocation',
        type: 'select',
        placeholder: 'Location',
        options: this.locationOptions,
        optionLabel: 'label',
        optionValue: 'value',
        value: this.selectedLocation,
        loading: this.isLoadingLocations,
        showInAccordion: true,
        showInDialog: false
      }
    ];
  }

  getClientFilterValue(): any {
    if (!this.selectedClient) {
      return null;
    }
    const client = this.clientOptions.find(c => String(c.id || c.value) === String(this.selectedClient)) ||
      this.clientSuggestions.find(c => String(c.value || c.id) === String(this.selectedClient));
    if (client) {
      return {
        value: client.id || client.value,
        label: client.client_name || client.company_name || client.label
      };
    }
    return null;
  }

  async handleClientSearch(event: { query: string; filter: any }) {
    const query = event.query || '';
    try {
      const currentClientId = this.selectedClient;
      const result = await ClientTypeaheadUtils.searchClients(
        query,
        this.clientListService,
        currentClientId,
        this.clientOptions,
        50
      );
      this.clientSuggestions = result.options;
      if (result.selectedClient && !this.clientOptions.find(c => String(c.id || c.value) === String(result.selectedClient?.value))) {
        this.clientOptions.push(result.selectedClient);
      }
      const clientFilter = this.filterConfigs.find(f => f.key === 'selectedClient');
      if (clientFilter) {
        clientFilter.suggestions = result.options;
      }
    } catch (error) {
      console.error('Error searching clients:', error);
      this.clientSuggestions = [];
    }
  }

  async loadClientOrders(page: number = 1) {
    try {
      this.isLoading = true;
      this.pageIndex = page;

      const queryParams: any = {
        offset: (this.pageIndex - 1) * this.rowsPerPage,
        limit: this.rowsPerPage,
        get_references: 'true',
        include_cancelled: 'true', // Include cancelled orders in list view (they'll be styled differently)
      };

      if (this.orderNumber && this.orderNumber.trim()) {
        queryParams['order_no'] = this.orderNumber.trim();
      }

      if (this.selectedClient && this.selectedClient.trim()) {
        queryParams['client_id'] = this.selectedClient.trim();
      }

      if (this.fromDate instanceof Date) {
        queryParams['from_date'] = moment(this.fromDate).format('YYYY-MM-DD');
      }

      if (this.toDate instanceof Date) {
        queryParams['to_date'] = moment(this.toDate).format('YYYY-MM-DD');
      }

      if (this.status && this.status.trim()) {
        queryParams.status = this.status.trim();
      }

      if (this.selectedLocation && this.selectedLocation.trim()) {
        queryParams['location_id'] = this.selectedLocation.trim();
      }

      const response = await lastValueFrom(this.clientOrderService.getClientOrders(queryParams));

      // Extract column preference from response
      if (response?.data?.column_preference) {
        const columnPreference = response.data.column_preference;
        this.columnPreferenceId = columnPreference.id ? String(columnPreference.id) : undefined;

        // Update columnConfigs with columns_list from preference
        if (columnPreference.columns_list && Array.isArray(columnPreference.columns_list)) {
          this.columnConfigs = columnPreference.columns_list.map((col: any) => ({
            key: col.key,
            header: col.header || col.label,
            type: col.type,
            ellipsis: col.ellipsis,
            align: col.align,
            is_selected: col.is_selected !== false,
          }));
        }
      }

      if (response?.status === 'success') {
        const rows = response?.data?.orders || [];
        this.tableData = rows.map((row: any) => ({
          ...row,
          id: row.id,
          order_no: row.order_no ?? row.id ?? '-',
          order_date: row.order_date ? moment(row.order_date).format('DD/MM/YYYY') : '-',
          client_code: row?.client_code ?? '-',
          client_name: row?.client_name ?? '-',
          project: row.project_name ?? '-',
          location: row.location_name ?? row.location ?? '-',
          memo: row.memo ?? '-',
          gross_amount: row.gross_amount ?? '-',
          final_amount: row.final_amount ?? '-',
          status: this.formatStatus(row.status || 'open'),
          approval_status: row.approval_status ? String(row.approval_status).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '-',
          is_cancelled: row.is_cancelled || false,
          center_id: row?.center_id || '-',
          client_id: row?.client_id || '-',
          created_by: row?.created_by || '-',
          discount_amount: row?.discount_amount || '-',
          form_template_id: row?.form_template_id || '-',
          place_of_supply_id: row?.place_of_supply_id || '-',
          product: row?.product || '-',
          sales_type: row?.sales_type || '-',
          ship_to_id: row?.ship_to_id || '-',
          subsidiary_id: row?.subsidiary_id || '-',
          tax_amount: row?.tax_amount || '-',
          updated_by: row?.updated_by || '-',
          location_name: row?.location_name || '-',
          project_name: row?.project_name || '-',
          created_at: row?.created_at || '-',
          receive_by_date: row?.receive_by_date || '-',
          updated_at: row?.updated_at || '-',
          client_category: row?.client_category || '-',
          client_type: row?.client_type || '-',
          delivery_date: row?.delivery_date || '-',
          payment_mode: row?.payment_mode || '-',
          payment_reference: row?.payment_reference || '-',
          phone_number: row?.phone_number || '-',
          raw: row
        }));
        this.totalCount = response?.data?.total ?? rows.length ?? 0;
        const calculatedPages = this.totalCount && this.rowsPerPage
          ? Math.ceil(this.totalCount / this.rowsPerPage)
          : 0;
        this.totalPages = calculatedPages > 0 ? calculatedPages : (this.tableData.length > 0 ? 1 : 0);
      } else {
        this.tableData = [];
        this.totalCount = 0;
        this.totalPages = 0;
      }
    } catch (error) {
      console.error('Failed to load client sales orders', error);
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
    } finally {
      this.isLoading = false;
    }
  }

  async loadClients() {
    try {
      this.isLoadingClients = true;
      const response = await lastValueFrom(this.clientListService.getClients({ limit: 200, offset: 0, include_cancelled: 'false' }));
      const list = response?.data?.clients || response?.data || [];
      this.clientOptions = Array.isArray(list)
        ? list.map((client: any) => ({
          label: client.client_name || client.client_code || 'Unknown',
          value: String(client.id ?? client.clientId ?? client.client_id ?? '')
        })).filter(option => option.value)
        : [];
      this.initializeFilterConfigs();
    } catch (error) {
      console.error('Failed to load clients for filter', error);
      this.clientOptions = [];
    } finally {
      this.isLoadingClients = false;
    }
  }

  exportXlsx() {
    this.openExportDialog('excel');
  }

  exportPdf() {
    window.print();
  }

  exportCsv() {
    this.openExportDialog('csv');
  }

  /**
   * Opens the export dialog with filter and column options.
   * @param format Default export format
   */
  private openExportDialog(format: 'excel' | 'csv'): void {
    const dialogRef = this.dialogService.open(ExportDialogComponent, {
      header: 'Export Sales Orders',
      width: '500px',
      data: {
        module: 'client_order',
        columns: this.columnConfigs,
        filters: this.getCurrentFilters(),
        defaultFormat: format
      }
    });

    if (dialogRef) {
      dialogRef.onClose.subscribe((request: CreateExportJobRequest) => {
        if (request) {
          this.startExport(request);
        }
      });
    }
  }

  /**
   * Gets the current filter values for the export.
   */
  private getCurrentFilters(): Record<string, any> {
    return {
      order_no: this.orderNumber.trim() || undefined,
      from_date: this.fromDate
        ? moment(this.fromDate).format('YYYY-MM-DD')
        : undefined,
      to_date: this.toDate
        ? moment(this.toDate).format('YYYY-MM-DD')
        : undefined,
      client_id: this.selectedClient || undefined,
      status: this.status || undefined,
      location_id: this.selectedLocation || undefined,
    };
  }

  /**
   * Starts the export job via the API.
   */
  private startExport(request: CreateExportJobRequest): void {
    this.exportApiService.createExportJob(request).subscribe({
      next: () => {
        this.toasterService.success(
          'Export Started',
          'Your export is processing. Click here to view progress →',
          5000
        );
        // Auto-open exports page in new tab after a short delay
        setTimeout(() => openInNewTab('/exports'), 1500);
      },
      error: (err: any) => {
        const message = err?.error?.message || 'Failed to start export';
        this.toasterService.error('Export Failed', message);
      }
    });
  }

  view(orderId: string) {
  if (!orderId) return;

  this.mastersService.openInNewTab(
    ['/clients/sales-order'],
    { soId: orderId, mode: 'view' }
  );
}
  edit(orderId: string) {
  if (!orderId) return;

  this.mastersService.openInNewTab(
    ['/clients/sales-order'],
    { soId: orderId, mode: 'edit' }
  );
}

  
  async delete(orderId: string) {
    if (!orderId) return;
    if (!confirm('Are you sure you want to delete this sales order?')) return;
    try {
      this.isLoading = true;
      await lastValueFrom(this.clientOrderService.deleteClientOrder(orderId));
      await this.loadClientOrders(this.pageIndex);
    } catch (error) {
      console.error('Failed to delete sales order', error);
      this.isLoading = false;
    }
  }

  async cancel(orderId: string) {
    if (!orderId) return;
    if (!confirm('Are you sure you want to cancel this sales order?')) return;
    try {
      this.isLoading = true;
      await lastValueFrom(this.clientOrderService.cancelClientOrder(orderId));
      this.toasterService.success('Success', 'Sales Order cancelled successfully');
      await this.loadClientOrders(this.pageIndex);
    } catch (e: any) {
      console.error('Failed to cancel sales order', e);
      this.toasterService.error('Error', e?.error?.message || 'Failed to cancel Sales Order');
      this.isLoading = false;
    }
  }

  navigateToSalesInvoice(soId: string) {
  if (!soId) return;

  this.mastersService.openInNewTab(
    ['/clients/client-invoice'],
    { clientOrderId: soId, mode: 'create' }
  );
}
  

  onFilterEvent(event: FilterEvent) {
    this.orderNumber = event.data?.orderNumber || '';
    this.fromDate = event.data?.fromDate || null;
    this.toDate = event.data?.toDate || null;

    const clientValue = event.data?.selectedClient;
    if (clientValue && clientValue.value) {
      this.selectedClient = clientValue.value;
    } else {
      this.selectedClient = '';
    }

    this.status = event.data?.status || '';
    this.selectedLocation = event.data?.selectedLocation || '';

    // Validate that from date is not after to date
    if (this.fromDate && this.toDate) {
      const from = moment(this.fromDate);
      const to = moment(this.toDate);
      if (from.isAfter(to)) {
        this.toasterService.warn('Warning', 'From Date cannot be after To Date');
        this.toDate = null;
        this.initializeFilterConfigs();
        return;
      }
    }

    // Reset to page 1 when filters change
    this.listStateService.clearPage();
    // Always reload when filters change (URL subscription may not trigger if already on page 1)
    this.loadClientOrders(1);
  }

  onActionClicked(event: ActionEvent) {
    switch (event.action) {
      case 'view':
        this.view(event.row.id);
        break;

      case 'edit':
        // Prevent editing if order is approved
        if (event.row.approval_status === 'approved') {
          this.toasterService.warn('Warning', 'Cannot edit sales order with approved status');
          return;
        }
        // Prevent editing if order status is closed or billed
        const orderStatus = String(event.row.raw?.status || event.row.status || '').toLowerCase();
        if (orderStatus === 'closed' || orderStatus === 'billed') {
          this.toasterService.warn('Warning', 'Cannot edit sales order with closed or billed status');
          return;
        }
        this.edit(event.row.id);
        break;
      case 'createInvoice':
        this.navigateToSalesInvoice(event.row.id);
        break;
      case 'delete':
        this.delete(event.row.id);
        break;
      case 'cancel':
        this.cancel(event.row.id);
        break;
    }
  }

  onPageChanged(page: number) {
    // Update URL - this triggers the subscription which loads the data
    this.listStateService.setPage(page);
  }

  async onColumnsSelected(selectedColumns: string[]) {
    // Placeholder for future backend integration with column preferences
    await this.loadClientOrders(this.pageIndex);
  }

  onColumnPreferenceSaved(event: { id: string; columns: ColumnConfig[] }) {
    this.columnPreferenceId = event.id ? String(event.id) : undefined;
    this.columnConfigs = event.columns;
  }

  formatStatus(status: string): string {
    if (!status) return 'Open';
    // Convert snake_case to Title Case
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

}